import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { aiService } from '../../services/aiService';
import { Loader2, Clock, TrendingUp, Sparkles, Target } from 'lucide-react';
import { motion } from 'framer-motion';

const BestTimeToPost: React.FC = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['bestTimeToPost'],
    queryFn: () => aiService.getBestTimeToPost(),
  });

  return (
    <div className="relative">
      <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-cyan-500/10 rounded-2xl blur"></div>
      <div className="relative bg-white dark:bg-slate-800/40 backdrop-blur-xl p-6 rounded-2xl border border-slate-200/50 dark:border-slate-600/30 shadow-xl">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Best Times to Post</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">AI-powered recommendations</p>
          </div>
        </div>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-12 h-12 border-2 border-slate-300 dark:border-slate-600 border-t-emerald-500 rounded-full animate-spin mb-4"></div>
            <p className="text-slate-500 dark:text-slate-400">Analyzing optimal times...</p>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-red-400" />
            </div>
            <p className="text-red-500 dark:text-red-400 font-medium">Error loading suggestions</p>
          </div>
        ) : !data?.suggestions || data.suggestions.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-slate-200/50 dark:bg-slate-700/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Target className="w-10 h-10 text-slate-500 dark:text-slate-500" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-300 mb-2">No recommendations yet</h3>
            <p className="text-slate-500 dark:text-slate-400">Post more content to get AI insights</p>
          </div>
        ) : (
          <div className="space-y-3">
            {data.suggestions.map((suggestion, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="group flex justify-between items-center p-4 bg-white dark:bg-slate-700/30 hover:bg-slate-100 dark:hover:bg-slate-700/50 rounded-xl border border-slate-200/50 dark:border-slate-600/30 hover:border-emerald-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/10"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-emerald-500/10 rounded-lg flex items-center justify-center">
                    <Clock className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-200 transition-colors">
                      {suggestion.platform}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Platform</p>
                  </div>
                </div>
                
                <div className="text-right">
                  <p className="text-sm font-medium text-emerald-500 dark:text-emerald-400 group-hover:text-emerald-600 dark:group-hover:text-emerald-300 transition-colors">
                    {suggestion.time}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Optimal time</p>
                </div>
                
                <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                  <Sparkles className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BestTimeToPost;